import React, { useCallback, useEffect, useState } from "react";
import { RotateCcw, PlayCircle, Zap, Clock, CheckCircle2, XCircle, AlertTriangle, MessageSquare, Phone, Lock } from "lucide-react";
import { PageHeader, Button, Select, Card, CardContent, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError, type RecoveryConfig, type RecoveryCampaign, type RecoveryMetrics } from "@/lib/api";

const FILTERS = [
  { value: "today", label: "Today" }, { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 days" }, { value: "last_30_days", label: "Last 30 days" },
  { value: "this_month", label: "This month" },
];
const money = (n: number) => Number(n || 0).toLocaleString();

const STEP_META: Record<string, { label: string; tone: "green" | "amber" | "red" | "slate" | "blue"; icon: typeof CheckCircle2 }> = {
  sent: { label: "Sent", tone: "green", icon: CheckCircle2 },
  unavailable: { label: "Unavailable — Config Required", tone: "amber", icon: AlertTriangle },
  skipped: { label: "Skipped", tone: "slate", icon: XCircle },
  pending: { label: "Scheduled", tone: "blue", icon: Clock },
};
const CAMP_TONE: Record<string, "green" | "amber" | "red" | "slate" | "blue"> = {
  recovery_active: "blue", recovered: "green", lost: "slate",
};

export default function Recovery() {
  const [config, setConfig] = useState<RecoveryConfig | null>(null);
  const [metrics, setMetrics] = useState<RecoveryMetrics | null>(null);
  const [campaigns, setCampaigns] = useState<RecoveryCampaign[] | null>(null);
  const [filter, setFilter] = useState("last_30_days");
  const [processing, setProcessing] = useState(false);

  const loadCampaigns = useCallback(() => {
    api.recoveryCampaigns().then(setCampaigns).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { api.recoveryConfig().then(setConfig).catch(() => {}); }, []);
  useEffect(() => { api.recoveryMetrics(filter).then(setMetrics).catch(() => {}); }, [filter]);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const runNow = async () => {
    setProcessing(true);
    try {
      const r = await api.recoveryProcess();
      toast.success(`Processed ${r.campaigns_scanned} active campaign(s)`);
      loadCampaigns();
      api.recoveryMetrics(filter).then(setMetrics).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); } finally { setProcessing(false); }
  };

  const stopCampaign = async (leadId: string) => {
    try { await api.recoveryStop(leadId); toast.success("Campaign stopped"); loadCampaigns(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cards = metrics ? [
    { label: "Leads entered", value: metrics.leads_entered, icon: Zap },
    { label: "Recovery attempts (incl. deferred)", value: metrics.recovery_attempts, icon: RotateCcw },
    { label: "Recovered leads", value: metrics.recovered_leads, icon: CheckCircle2 },
    { label: "Appointments recovered", value: metrics.appointments_recovered, icon: PlayCircle },
    { label: "Recovery rate", value: `${metrics.recovery_rate}%`, icon: Zap },
  ] : [];

  return (
    <div className="space-y-6" data-testid="page-recovery">
      <PageHeader title="Lead Recovery" description="Automatically follow up with leads who didn't book the first time." testid="recovery-header"
        action={<Button onClick={runNow} disabled={processing} data-testid="recovery-process-btn"><RotateCcw className="h-4 w-4" /> {processing ? "Processing…" : "Run recovery now"}</Button>} />

      {/* Config / status banner */}
      {config && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="recovery-config">
          <StatusBadge tone="blue">{config.plan} plan</StatusBadge>
          <span className="text-xs text-slate-500">{config.steps.length}-step sequence · up to {config.max_steps} steps</span>
          {config.sms_configured ? (
            <StatusBadge tone="green"><MessageSquare className="mr-1 inline h-3 w-3" /> SMS live</StatusBadge>
          ) : (
            <StatusBadge tone="amber"><AlertTriangle className="mr-1 inline h-3 w-3" /> SMS: configuration required</StatusBadge>
          )}
          <StatusBadge tone="slate"><Phone className="mr-1 inline h-3 w-3" /> Voice recovery: not yet available</StatusBadge>
        </div>
      )}

      {!config?.sms_configured && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="recovery-honest-note">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">
            Recovery steps are scheduled and processed automatically, but SMS sending is paused until Twilio is fully activated (KYC/A2P&nbsp;10DLC).
            Due steps are marked <b>“Unavailable — Configuration Required”</b> and are <b>never</b> recorded as sent. They will auto-execute once Twilio goes live — no rebuild needed.
          </p>
        </div>
      )}

      {/* Metrics */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800">Performance</h2>
        <div className="w-44"><Select value={filter} onChange={setFilter} options={FILTERS} testid="recovery-metrics-filter" /></div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="recovery-metrics">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`recovery-metric-${c.label.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500"><c.icon className="h-4 w-4 text-blue-500" /> {c.label}</div>
            <div className="mt-2 font-display text-2xl font-semibold text-slate-900">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Campaigns */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Recovery campaigns</h2>
        {campaigns === null ? <LoadingBlock rows={4} /> : campaigns.length === 0 ? (
          <EmptyState icon={RotateCcw} title="No recovery campaigns yet" description="Enroll a lead who didn't book to start an automated follow-up sequence." />
        ) : (
          <div className="space-y-3" data-testid="recovery-campaigns">
            {campaigns.map((c) => (
              <Card key={c.id} data-testid={`recovery-campaign-${c.id}`}>
                <CardContent className="p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <StatusBadge tone={CAMP_TONE[c.status] ?? "slate"}>{c.status.replace(/_/g, " ")}</StatusBadge>
                      <span className="text-xs text-slate-500">{c.plan} plan · {c.steps.length} steps</span>
                      {c.stop_reason && <StatusBadge tone="slate">stopped: {c.stop_reason}</StatusBadge>}
                    </div>
                    {c.status === "recovery_active" && (
                      <Button size="sm" variant="outline" onClick={() => stopCampaign(c.lead_id)} data-testid={`recovery-stop-${c.id}`}>Stop</Button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {c.steps.map((s, i) => {
                      const meta = STEP_META[s.state] ?? STEP_META.pending;
                      const Icon = s.channel === "voice" ? Phone : (meta.icon);
                      return (
                        <div key={i} className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs" data-testid={`recovery-step-${c.id}-${i}`}>
                          <Icon className="h-3.5 w-3.5 text-slate-400" />
                          <span className="capitalize text-slate-600">{s.channel}</span>
                          <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
                          {s.attempts ? <span className="text-slate-400">×{s.attempts}</span> : null}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
